import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Loader2, Plus, X, FileImage, Trash2, Camera } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/format";
import { Skeleton } from "../../components/ui/skeleton";
import { toast } from "sonner";

const opts = { withCredentials: true };

// Fix CJ (2026-09-11): también se abre desde Tareas, con la tarea ya elegida.
export function NuevoInformeModal({ obraId, tareas, onClose, onSaved, tareaInicial = "" }) {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [tareaId, setTareaId] = useState(tareaInicial);
  const [fotos, setFotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!titulo.trim()) { toast.error("Ingresá un título"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("titulo", titulo);
      fd.append("descripcion", descripcion);
      if (tareaId) fd.append("tarea_id", tareaId);
      // Fix N (2026-08-31): el backend acepta y guarda nivel_id desde siempre
      // (obra_informes.py:81 y :99) y tiene filtro por nivel, pero el formulario
      // nunca lo mandaba, así que ese filtro no servía para nada. El nivel se
      // deduce de la tarea elegida, que ya lo trae resuelto.
      const tareaSel = tareas.find(t => t.id === tareaId);
      if (tareaSel?.nivel_id) fd.append("nivel_id", tareaSel.nivel_id);
      fotos.forEach(f => fd.append("fotos", f));
      await axios.post(`${API}/obras/${obraId}/informes`, fd, opts);
      toast.success("Informe creado");
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear el informe"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto" data-testid="informes-nuevo-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Nuevo informe</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Título" className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="informes-titulo-input" />
          <textarea value={descripcion} onChange={e => setDescripcion(e.target.value)} placeholder="Descripción" rows={3} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm" data-testid="informes-descripcion-input" />
          <select value={tareaId} onChange={e => setTareaId(e.target.value)} className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm bg-white" data-testid="informes-tarea-select">
            <option value="">Sin tarea enlazada</option>
            {tareas.map(t => <option key={t.id} value={t.id}>{t.item_numero} — {t.tarea_nombre}</option>)}
          </select>
          <label className="flex items-center gap-2 border border-dashed border-gray-300 rounded-xl px-3 py-3 text-sm text-gray-500 cursor-pointer justify-center hover:border-[#EA580C]">
            <Camera size={15} /> {fotos.length ? `${fotos.length} foto(s) seleccionada(s)` : "Adjuntar fotos"}
            <input type="file" accept="image/*" multiple className="hidden" onChange={e => setFotos(Array.from(e.target.files || []))} data-testid="informes-fotos-input" />
          </label>
        </div>
        <button onClick={submit} disabled={saving} className="w-full mt-5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2" data-testid="informes-crear-btn">
          {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear informe
        </button>
      </div>
    </div>, document.body
  );
}

export default function InformesTab({ obraId }) {
  const [informes, setInformes] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/obras/${obraId}/informes`, opts),
      axios.get(`${API}/obras/${obraId}/tareas`, opts),
    ]).then(([i, t]) => { setInformes(i.data.informes || []); setTareas(t.data.tareas || []); })
      .catch(() => toast.error("Error cargando informes"))
      .finally(() => setLoading(false));
  }, [obraId]);
  useEffect(load, [load]);

  const eliminar = async (id) => {
    try {
      await axios.delete(`${API}/obras/${obraId}/informes/${id}`, opts);
      toast.success("Informe eliminado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="space-y-2">{[1, 2].map(i => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}</div>;

  return (
    <div className="space-y-4" data-testid="obra-informes-tab">
      <div className="flex justify-end">
        <button onClick={() => setShowModal(true)} className="flex items-center gap-1.5 bg-[#EA580C] hover:bg-[#C2410C] text-white rounded-lg px-3 py-1.5 text-xs font-semibold" data-testid="informes-nuevo-btn">
          <Plus size={13} /> Nuevo informe
        </button>
      </div>
      {informes.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200/60 p-8 text-center shadow-sm">
          <FileImage size={36} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-600 font-medium">Sin informes todavía</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {informes.map(inf => (
            <div key={inf.id} className="bg-white rounded-2xl border border-gray-200/60 p-4 shadow-sm" data-testid={`informes-card-${inf.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-bold text-gray-900">{inf.titulo}</p>
                  <p className="text-xs text-gray-400">{formatDate(inf.fecha)} · {inf.created_by_name}</p>
                </div>
                <button onClick={() => eliminar(inf.id)} className="text-gray-300 hover:text-red-500" data-testid={`informes-del-btn-${inf.id}`}><Trash2 size={14} /></button>
              </div>
              <p className="text-sm text-gray-600 mt-2">{inf.descripcion}</p>
              {inf.fotos?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {inf.fotos.map((f, i) => (
                    <img key={i} src={`${API}/obras/${obraId}/informes/${inf.id}/fotos/${i}`} alt={f.filename} className="w-16 h-16 object-cover rounded-lg border border-gray-100" data-testid={`informes-foto-${inf.id}-${i}`} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {showModal && <NuevoInformeModal obraId={obraId} tareas={tareas} onClose={() => setShowModal(false)} onSaved={load} />}
    </div>
  );
}
